import { useState, useMemo, useEffect } from 'react';
import { Button, Panel } from '@/components/ui';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOURS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21];

export interface RescheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (newDate: Date) => Promise<void>;
  currentDate: Date;
  workoutName?: string;
}

export function RescheduleModal({
  isOpen,
  onClose,
  onSave,
  currentDate,
}: RescheduleModalProps) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // Check if session is within 24 hours
  const hoursUntilSession = useMemo(() => {
    const now = new Date();
    return (currentDate.getTime() - now.getTime()) / (1000 * 60 * 60);
  }, [currentDate]);

  const isWithin24Hours = hoursUntilSession < 24;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedDate(null);
      setSelectedTime(null);
    }
  }, [isOpen]);

  // Generate next 14 days (2 weeks)
  const availableDays = useMemo(() => {
    const days: Date[] = [];
    const today = new Date();

    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push(date);
    }

    return days;
  }, []);

  const formatHour = (hour: number): string => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
  };

  const formatDate = (date: Date): string => {
    const dayName = DAY_NAMES[date.getDay()];
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${dayName}, ${month} ${day}`;
  };

  const handleSave = async () => {
    if (!selectedDate || selectedTime === null) return;

    setSaving(true);
    try {
      // Create scheduled datetime
      const scheduledDateTime = new Date(selectedDate);
      scheduledDateTime.setHours(selectedTime, 0, 0, 0);

      await onSave(scheduledDateTime);
      onClose();
    } catch (error) {
      console.error('Error rescheduling:', error);
      alert('Failed to reschedule. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // If session is within 24 hours, show locked message
  if (isWithin24Hours) {
    return (
      <Panel
        isOpen={isOpen}
        onClose={onClose}
        title="Cannot Reschedule"
        variant="modal"
        size="md"
      >
        <div className="space-y-6">
          {/* Warning Icon */}
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/20 rounded-lg flex-shrink-0">
              <svg
                className="w-6 h-6 text-amber-600 dark:text-amber-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                Session starts in less than 24 hours
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400">
                For fairness to your trainer and to maintain accountability, sessions cannot be rescheduled within 24 hours of their start time.
              </p>
            </div>
          </div>

          {/* Current session info */}
          <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-4 border border-gray-200 dark:border-white/10">
            <p className="text-sm text-gray-600 dark:text-slate-400 mb-1">
              Scheduled time
            </p>
            <p className="font-semibold text-gray-900 dark:text-white">
              {formatDate(currentDate)} at {formatHour(currentDate.getHours())}
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
              Starts in {Math.max(0, hoursUntilSession).toFixed(1)} hours
            </p>
          </div>

          {/* Info box */}
          <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900/30 rounded-lg p-4">
            <div className="flex gap-3">
              <svg
                className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-sm text-blue-900 dark:text-blue-300">
                If you need to cancel or reschedule, please contact your trainer directly. For future sessions, make sure to reschedule at least 24 hours in advance.
              </div>
            </div>
          </div>

          {/* Close button */}
          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-white/10">
            <Button variant="primary" onClick={onClose} className="w-full sm:w-auto">
              Got it
            </Button>
          </div>
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      isOpen={isOpen}
      onClose={onClose}
      title="Reschedule Workout"
      variant="modal"
      size="lg"
    >
      <div className="space-y-6">
        {/* Current time info */}
        <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-4 border border-gray-200 dark:border-white/10">
          <p className="text-sm text-gray-600 dark:text-slate-400 mb-1">
            Current time
          </p>
          <p className="font-semibold text-gray-900 dark:text-white">
            {formatDate(currentDate)} at {formatHour(currentDate.getHours())}
          </p>
        </div>

        {/* Day Selection */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
            Select a new day
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-h-64 overflow-y-auto">
            {availableDays.map((day, index) => {
              const isSelected = selectedDate?.toDateString() === day.toDateString();
              return (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedDate(day);
                    setSelectedTime(null); // Reset time when changing day
                  }}
                  className={`
                    p-4 rounded-xl border-2 transition-all text-center
                    ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-white/10 hover:border-primary-300 dark:hover:border-primary-700'
                    }
                  `}
                >
                  <div className="text-xs text-gray-600 dark:text-slate-400 mb-1">
                    {DAY_NAMES[day.getDay()]}
                  </div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white">
                    {day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Time Selection */}
        {selectedDate && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
              Select a time on {formatDate(selectedDate)}
            </h3>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
              {HOURS.map((hour) => {
                const isSelected = selectedTime === hour;
                return (
                  <button
                    key={hour}
                    onClick={() => setSelectedTime(hour)}
                    className={`
                      px-3 py-2 rounded-lg text-sm font-medium transition-all
                      ${
                        isSelected
                          ? 'bg-primary-600 text-white shadow-md shadow-primary-600/25'
                          : 'bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/10'
                      }
                    `}
                  >
                    {formatHour(hour)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Selected Time Summary */}
        {selectedDate && selectedTime !== null && (
          <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-900/30 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <svg
                className="w-6 h-6 text-primary-600 dark:text-primary-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="text-sm text-primary-900 dark:text-primary-300 font-medium">
                  New time
                </p>
                <p className="text-lg font-bold text-primary-600 dark:text-primary-400">
                  {formatDate(selectedDate)} at {formatHour(selectedTime)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-white/10">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!selectedDate || selectedTime === null || saving}
            isLoading={saving}
            className="flex-1"
          >
            {saving ? 'Saving...' : 'Reschedule'}
          </Button>
        </div>
      </div>
    </Panel>
  );
}
